export type EventTimeframe = 'all' | 'upcoming' | 'past';

export type EventCardItem = {
  id: number;
  title: string;
  description: string;
  dateLabel: string;
  dateValue: Date | null;
  place: string;
  eventType: string;
  link: string | null;
  thumbnailUrl: string | null;
  thumbnailAlt: string;
  year: string;
};

export type EventFilters = {
  query: string;
  eventType: string;
  place: string;
  year: string;
  timeframe: EventTimeframe;
};

const ACF_DATE_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

export const startOfToday = (now = new Date()) => new Date(now.getFullYear(), now.getMonth(), now.getDate());

export const parseAcfDate = (value: string): Date | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const match = trimmed.match(ACF_DATE_PATTERN);
  if (match) {
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const parsed = new Date(year, month - 1, day);
    if (
      parsed.getFullYear() === year &&
      parsed.getMonth() === month - 1 &&
      parsed.getDate() === day &&
      !Number.isNaN(parsed.getTime())
    ) {
      return parsed;
    }
  }

  const fallback = new Date(trimmed);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

export const formatEventDate = (date: Date) =>
  date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

export const isUpcomingEvent = (date: Date | null, today = startOfToday()) => {
  if (!date) return false;
  return date.getTime() >= today.getTime();
};

export const uniqueSorted = (values: string[]) =>
  [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }));

export const filterEvents = (
  events: EventCardItem[],
  { query, eventType, place, year, timeframe }: EventFilters,
  today = startOfToday(),
) => {
  const normalizedQuery = query.trim().toLowerCase();

  return events.filter(event => {
    if (eventType !== 'all' && event.eventType !== eventType) return false;
    if (place !== 'all' && event.place !== place) return false;
    if (year !== 'all' && event.year !== year) return false;

    if (timeframe === 'upcoming' && !isUpcomingEvent(event.dateValue, today)) return false;
    if (timeframe === 'past' && isUpcomingEvent(event.dateValue, today)) return false;

    if (!normalizedQuery) return true;
    return `${event.title} ${event.description} ${event.place} ${event.eventType}`
      .toLowerCase()
      .includes(normalizedQuery);
  });
};

export const splitEventsByTime = (events: EventCardItem[], today = startOfToday()) => {
  const upcoming = events
    .filter(event => isUpcomingEvent(event.dateValue, today))
    .sort((a, b) => (a.dateValue?.getTime() ?? 0) - (b.dateValue?.getTime() ?? 0));

  const recent = events
    .filter(event => !isUpcomingEvent(event.dateValue, today))
    .sort((a, b) => (b.dateValue?.getTime() ?? 0) - (a.dateValue?.getTime() ?? 0));

  return { upcoming, recent };
};
